/**
 * Track Plugin Chain Component
 * VST-style plugin chain for individual tracks
 * Integrates with the full plugin system for professional audio processing
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Button } from '../../ui/button';
import { Slider } from '../../ui/slider';
import {
  Plus,
  X,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Power,
  Settings2,
  Copy,
  Trash2,
  RotateCcw,
  Activity,
  Sliders,
  Waves,
  Timer,
  Music2,
  Volume2,
  FolderOpen,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { PLUGIN_CATEGORIES, PARAM_TYPES } from '../plugins/PluginSystem';

// Icon mapping for plugin categories
const CATEGORY_ICONS = {
  dynamics: Activity,
  eq: Sliders,
  modulation: Music2,
  delay: Timer,
  reverb: Waves,
  utility: Volume2,
};

// Color mapping for plugin categories
const CATEGORY_COLORS = {
  dynamics: 'from-red-500 to-orange-500',
  eq: 'from-blue-500 to-cyan-500',
  modulation: 'from-pink-500 to-rose-500',
  delay: 'from-emerald-500 to-teal-500',
  reverb: 'from-purple-500 to-indigo-500',
  utility: 'from-zinc-500 to-zinc-400',
};

/**
 * Compact Plugin Slot for Track Chains
 */
const TrackPluginSlot = ({
  plugin,
  onToggleBypass,
  onRemove,
  onSelect,
  isSelected,
  isDragging,
}) => {
  const IconComponent = CATEGORY_ICONS[plugin.category] || Activity;
  const colorClass = CATEGORY_COLORS[plugin.category] || 'from-zinc-500 to-zinc-400';
  
  return (
    <motion.div
      layout
      className={cn(
        "relative group flex items-center gap-1 px-1.5 py-1 rounded text-[9px] font-medium transition-all",
        plugin.enabled ? `bg-gradient-to-r ${colorClass} bg-opacity-20` : 'bg-zinc-800/80',
        plugin.enabled ? 'border-white/20' : 'border-zinc-600/50',
        "border",
        isSelected && "ring-1 ring-cyan-400/50",
        isDragging && "shadow-lg scale-105 z-50",
        !plugin.enabled && "opacity-60"
      )}
      data-testid={`track-plugin-${plugin.id}`}
    >
      {/* Drag Handle */}
      <div 
        className={cn(
          "cursor-grab active:cursor-grabbing p-0.5 -ml-0.5 rounded",
          "opacity-40 group-hover:opacity-100 transition-opacity"
        )}
      >
        <GripVertical className="w-2.5 h-2.5 text-white/70" />
      </div>
      
      {/* Plugin Icon & Name */}
      <div 
        className={cn(
          "flex items-center gap-1 cursor-pointer",
          plugin.enabled ? "text-white" : "text-zinc-400"
        )}
        onClick={() => onSelect?.(plugin)}
      >
        <div className={cn(
          "w-4 h-4 rounded flex items-center justify-center",
          plugin.enabled ? `bg-gradient-to-br ${colorClass}` : "bg-zinc-700"
        )}>
          <IconComponent className="w-2.5 h-2.5 text-white" />
        </div>
        <span className="max-w-[50px] truncate">{plugin.name}</span>
      </div>
      
      {/* Bypass Indicator */}
      {!plugin.enabled && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-full h-px bg-zinc-500 rotate-[-12deg]" />
        </div>
      )}
      
      {/* Action Buttons (visible on hover) */}
      <div className={cn(
        "flex items-center gap-0.5 ml-auto",
        "opacity-0 group-hover:opacity-100 transition-opacity"
      )}>
        {/* Bypass Toggle */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleBypass?.(plugin.id); }}
          className={cn(
            "w-4 h-4 rounded flex items-center justify-center transition-all",
            plugin.enabled 
              ? "bg-emerald-500 hover:bg-emerald-400" 
              : "bg-zinc-600 hover:bg-zinc-500"
          )}
          title={plugin.enabled ? "Bypass" : "Enable"}
          data-testid={`track-plugin-bypass-${plugin.id}`}
        >
          <Power className="w-2.5 h-2.5 text-white" />
        </button>
        
        {/* Remove Button */}
        <button
          onClick={(e) => { e.stopPropagation(); onRemove?.(plugin.id); }}
          className="w-4 h-4 rounded bg-red-500/80 hover:bg-red-500 flex items-center justify-center transition-all"
          title="Remove"
          data-testid={`track-plugin-remove-${plugin.id}`}
        >
          <X className="w-2.5 h-2.5 text-white" />
        </button>
      </div>
    </motion.div>
  );
};

/**
 * Plugin Parameter Editor (Expanded)
 */
const PluginParamEditor = ({ plugin, onParamChange, onClose }) => {
  if (!plugin) return null;
  
  const IconComponent = CATEGORY_ICONS[plugin.category] || Activity;
  const colorClass = CATEGORY_COLORS[plugin.category] || 'from-zinc-500 to-zinc-400';
  
  // Get parameters - handle both Map and object formats
  const getParameters = () => {
    if (plugin.parameters instanceof Map) {
      const params = [];
      plugin.parameters.forEach((param, id) => {
        params.push({ id, ...param });
      });
      return params;
    } else if (plugin.params) {
      // Simple effect format
      return Object.entries(plugin.params).map(([id, value]) => ({
        id,
        name: id.replace(/([A-Z])/g, ' $1').trim(),
        value,
        minValue: 0,
        maxValue: 1,
      }));
    }
    return [];
  };
  
  const parameters = getParameters();
  
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="overflow-hidden"
    >
      <div className={cn(
        "mt-2 p-2 rounded-lg border bg-zinc-800/50",
        "border-zinc-700"
      )}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <div className={cn(
              "w-5 h-5 rounded flex items-center justify-center bg-gradient-to-br",
              colorClass
            )}>
              <IconComponent className="w-3 h-3 text-white" />
            </div>
            <span className="text-[10px] font-semibold text-white">{plugin.name}</span>
          </div>
          <button
            onClick={onClose}
            className="w-4 h-4 rounded bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center transition-colors"
          >
            <X className="w-3 h-3 text-zinc-400" />
          </button>
        </div>
        
        {/* Presets (if available) */}
        {plugin.presets && plugin.presets.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {plugin.presets.slice(0, 5).map(preset => (
              <button
                key={preset.id}
                onClick={() => plugin.loadPreset?.(preset.id)}
                className={cn(
                  "px-1.5 py-0.5 rounded text-[8px] font-medium transition-colors",
                  "bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
                )}
              >
                {preset.name}
              </button>
            ))}
          </div>
        )}
        
        {/* Parameters */}
        <div className="space-y-2">
          {parameters.slice(0, 6).map(param => {
            const value = param.getValue ? param.getValue() : param.value;
            const min = param.minValue ?? 0;
            const max = param.maxValue ?? 1;
            
            return (
              <div key={param.id} className="flex items-center gap-2">
                <span className="text-[9px] text-zinc-400 w-14 truncate capitalize">
                  {param.name || param.id}
                </span>
                <Slider
                  value={[value]}
                  min={min}
                  max={max}
                  step={(max - min) / 100}
                  onValueChange={([v]) => onParamChange?.(plugin.id, param.id, v)}
                  className="flex-1 h-3"
                />
                <span className="text-[8px] text-zinc-500 w-8 text-right font-mono">
                  {typeof value === 'number' ? value.toFixed(2) : value}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

/**
 * Main Track Plugin Chain Component
 */
export const TrackPluginChain = ({
  trackId,
  trackName,
  plugins = [],
  onAddPlugin,
  onRemovePlugin,
  onToggleBypass,
  onReorderPlugins,
  onParamChange,
  onOpenBrowser,
  onOpenPresets, // NEW: Open preset selector
  isExpanded = false,
  onToggleExpand,
  compact = false,
  className,
}) => {
  const [selectedPluginId, setSelectedPluginId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  
  const handleReorder = (newOrder) => {
    onReorderPlugins?.(trackId, newOrder);
  };
  
  const selectedPlugin = plugins.find(p => p.id === selectedPluginId);
  const enabledCount = plugins.filter(p => p.enabled).length;
  
  if (compact) {
    // Compact view for mixer channel strips
    return (
      <div className={cn("w-full", className)} data-testid={`track-plugin-chain-${trackId}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <button
            onClick={onToggleExpand}
            className="flex items-center gap-1 text-[9px] text-zinc-400 hover:text-white transition-colors"
            data-testid={`track-plugin-toggle-${trackId}`}
          >
            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <span>Plugins</span>
            {plugins.length > 0 && (
              <span className={cn(
                "px-1 rounded text-[8px]",
                enabledCount > 0 ? "bg-violet-500/30 text-violet-300" : "bg-zinc-700 text-zinc-400"
              )}>
                {enabledCount}/{plugins.length}
              </span>
            )}
          </button>
          <div className="flex items-center gap-1">
            {/* Preset Button */}
            <button
              onClick={() => onOpenPresets?.(trackId)}
              className="w-5 h-5 rounded bg-zinc-800 hover:bg-violet-600/50 flex items-center justify-center transition-colors"
              title="Load Preset"
              data-testid={`track-presets-btn-${trackId}`}
            >
              <FolderOpen className="w-3 h-3 text-zinc-400" />
            </button>
            {/* Add Plugin Button */}
            <button
              onClick={() => onOpenBrowser?.(trackId)}
              className="w-5 h-5 rounded bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
              title="Add Plugin"
              data-testid={`track-add-plugin-${trackId}`}
            >
              <Plus className="w-3 h-3 text-zinc-400" />
            </button>
          </div>
        </div>
        
        {/* Plugin List */}
        {plugins.length > 0 ? (
          <Reorder.Group 
            axis="x" 
            values={plugins} 
            onReorder={handleReorder}
            className="flex flex-wrap gap-1"
          >
            {plugins.map((plugin) => (
              <Reorder.Item 
                key={plugin.id} 
                value={plugin}
                onDragStart={() => setDraggingId(plugin.id)}
                onDragEnd={() => setDraggingId(null)}
                whileDrag={{ scale: 1.05, zIndex: 50 }}
                className="cursor-grab active:cursor-grabbing"
              >
                <TrackPluginSlot
                  plugin={plugin}
                  onToggleBypass={(id) => onToggleBypass?.(trackId, id)}
                  onRemove={(id) => onRemovePlugin?.(trackId, id)}
                  onSelect={(p) => setSelectedPluginId(p.id === selectedPluginId ? null : p.id)}
                  isSelected={plugin.id === selectedPluginId}
                  isDragging={draggingId === plugin.id}
                />
              </Reorder.Item>
            ))}
          </Reorder.Group>
        ) : (
          <div className="text-[8px] text-zinc-600 text-center py-1">
            No plugins
          </div>
        )}
        
        {/* Selected Plugin Editor */}
        <AnimatePresence>
          {isExpanded && selectedPlugin && (
            <PluginParamEditor
              plugin={selectedPlugin}
              onParamChange={(pluginId, paramId, value) => onParamChange?.(trackId, pluginId, paramId, value)}
              onClose={() => setSelectedPluginId(null)}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }
  
  // Full view (for dedicated plugin panel)
  return (
    <div className={cn("flex flex-col h-full", className)} data-testid={`track-plugin-chain-${trackId}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-white">{trackName || `Track ${trackId}`}</span>
          <span className="text-xs text-zinc-500">Plugin Chain</span>
        </div>
        <Button
          onClick={() => onOpenBrowser?.(trackId)}
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          data-testid={`track-add-plugin-full-${trackId}`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add
        </Button>
      </div>
      
      {/* Plugin List */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        {plugins.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            <div className="text-sm mb-2">No plugins</div>
            <Button
              onClick={() => onOpenBrowser?.(trackId)}
              size="sm"
              variant="outline"
              className="text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Plugin
            </Button>
          </div>
        ) : (
          <Reorder.Group
            axis="y"
            values={plugins}
            onReorder={handleReorder}
            className="space-y-2"
          >
            {plugins.map((plugin) => (
              <Reorder.Item
                key={plugin.id}
                value={plugin}
                onDragStart={() => setDraggingId(plugin.id)}
                onDragEnd={() => setDraggingId(null)}
                className="cursor-grab active:cursor-grabbing"
              >
                <div className={cn(
                  "bg-zinc-800/80 rounded-lg border transition-all p-2",
                  plugin.enabled ? "border-zinc-700" : "border-zinc-700/50 opacity-60",
                  draggingId === plugin.id && "shadow-xl scale-[1.01] z-50"
                )}>
                  <div className="flex items-center gap-2">
                    <GripVertical className="w-4 h-4 text-zinc-500" />
                    
                    <div className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br",
                      CATEGORY_COLORS[plugin.category] || 'from-zinc-500 to-zinc-400',
                      !plugin.enabled && "grayscale"
                    )}>
                      {(() => {
                        const Icon = CATEGORY_ICONS[plugin.category] || Activity;
                        return <Icon className="w-4 h-4 text-white" />;
                      })()}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className={cn(
                        "text-sm font-medium truncate",
                        plugin.enabled ? "text-white" : "text-zinc-500"
                      )}>
                        {plugin.name}
                      </div>
                      <div className="text-[10px] text-zinc-500 capitalize">
                        {plugin.category}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onToggleBypass?.(trackId, plugin.id)}
                        className={cn(
                          "w-7 h-7 rounded flex items-center justify-center transition-colors",
                          plugin.enabled 
                            ? "bg-emerald-500 hover:bg-emerald-400 text-white" 
                            : "bg-zinc-700 hover:bg-zinc-600 text-zinc-400"
                        )}
                      >
                        <Power className="w-3.5 h-3.5" />
                      </button>
                      
                      <button
                        onClick={() => setSelectedPluginId(plugin.id === selectedPluginId ? null : plugin.id)}
                        className="w-7 h-7 rounded bg-zinc-700 hover:bg-zinc-600 flex items-center justify-center text-zinc-300"
                      >
                        {selectedPluginId === plugin.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      
                      <button
                        onClick={() => onRemovePlugin?.(trackId, plugin.id)}
                        className="w-7 h-7 rounded bg-red-500/20 hover:bg-red-500/40 flex items-center justify-center text-red-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Expanded Parameters */}
                  <AnimatePresence>
                    {selectedPluginId === plugin.id && (
                      <PluginParamEditor
                        plugin={plugin}
                        onParamChange={(pluginId, paramId, value) => onParamChange?.(trackId, pluginId, paramId, value)}
                        onClose={() => setSelectedPluginId(null)}
                      />
                    )}
                  </AnimatePresence>
                </div>
              </Reorder.Item>
            ))}
          </Reorder.Group>
        )}
      </div>
      
      {/* Footer */}
      {plugins.length > 0 && (
        <div className="px-3 py-2 border-t border-zinc-800 text-[10px] text-zinc-500">
          {plugins.length} plugin{plugins.length !== 1 ? 's' : ''} • {enabledCount} active
        </div>
      )}
    </div>
  );
};

export default TrackPluginChain;
